import React, { useEffect, useState } from "react";
import {
  Box, TextField, Button, Typography, Paper, Grid,
  Accordion, AccordionSummary, AccordionDetails, Chip, Autocomplete, MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { openAuthPdf } from "../services/openAuthPdf";
import api from "../services/api";
import { todayISO } from "../utils/date";

/* ================= DATE RANGE PDF BOX ================= */
function DateRangeBox({ title, endpoint }) {
  const today = todayISO();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const viewPDF = () => {
    openAuthPdf(`${endpoint}?fromDate=${fromDate}&toDate=${toDate}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="From" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="To" fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button variant="contained" fullWidth onClick={viewPDF}>View PDF</Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ================= GENERAL LEDGER BOX (date range + account picker) ================= */
function GeneralLedgerBox() {
  const today = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [accounts, setAccounts] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api.get("/accounts")
      .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load accounts:", e));
  }, []);

  const viewPDF = () => {
    const params = new URLSearchParams({ fromDate, toDate });
    if (picked?.SubAccount_Name) params.set("accountName", picked.SubAccount_Name);
    openAuthPdf(`pdfReports/generalLedger?${params.toString()}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>General Ledger</Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Leave Account empty for all accounts, or pick one for a single-account ledger.
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="From" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="To" fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button variant="contained" fullWidth onClick={viewPDF}>View PDF</Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Autocomplete
            options={accounts}
            getOptionLabel={(o) => o?.SubAccount_Name || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={picked}
            onChange={(_, v) => setPicked(v)}
            renderInput={(params) => (
              <TextField {...params} label="Account (optional)" placeholder="All accounts" fullWidth />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ============ INVOICE BOX (date range + account picker) ============ */
function InvoiceBox({ title, endpoint, partyLabel }) {
  const today = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [accounts, setAccounts] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api.get("/accounts")
      .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load accounts:", e));
  }, []);

  const viewPDF = () => {
    const params = new URLSearchParams({ fromDate, toDate });
    if (picked?.SubAccount_Name) params.set("accountName", picked.SubAccount_Name);
    openAuthPdf(`${endpoint}?${params.toString()}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Leave {partyLabel} empty for everyone, or pick one to get only their invoices.
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField type="date" label="From" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField type="date" label="To" fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button variant="contained" fullWidth onClick={viewPDF}>View PDF</Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Autocomplete
            options={accounts}
            getOptionLabel={(o) => o?.SubAccount_Name || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={picked}
            onChange={(_, v) => setPicked(v)}
            renderInput={(params) => (
              <TextField {...params} label={`${partyLabel} (optional)`}
                placeholder={`All ${partyLabel.toLowerCase()}s`} fullWidth />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ================= ITEM LEDGER BOX (date range + material picker) ================= */
function ItemLedgerBox() {
  const today = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [products, setProducts] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api.get("/products")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        // Sorted by name, because this list is read, not searched by id.
        setProducts([...list].sort((a, b) =>
          String(a.Product_Description || "").localeCompare(String(b.Product_Description || ""))));
      })
      .catch((e) => console.error("Failed to load products:", e));
  }, []);

  const viewPDF = () => {
    const params = new URLSearchParams({ fromDate, toDate });
    if (picked?.Product_Description) params.set("item", picked.Product_Description);
    openAuthPdf(`pdfReports/itemLedger?${params.toString()}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Item Ledger</Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Leave Material empty for every material, or pick one to follow a single
        material in and out on its own.
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="From" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="To" fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button variant="contained" fullWidth onClick={viewPDF}>View PDF</Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Autocomplete
            options={products}
            getOptionLabel={(o) => o?.Product_Description || ""}
            isOptionEqualToValue={(opt, val) => opt.Item_ID === val.Item_ID}
            value={picked}
            onChange={(_, v) => setPicked(v)}
            renderInput={(params) => (
              <TextField {...params} label="Material (optional)" placeholder="All materials" fullWidth />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ================= PURCHASES BREAKDOWN BOX (date range + group-by) ================= */
function PurchaseBreakdownBox() {
  const today = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [groupBy, setGroupBy] = useState("material");

  const viewPDF = () => {
    openAuthPdf(`pdfReports/purchaseBreakdown?fromDate=${fromDate}&toDate=${toDate}&groupBy=${groupBy}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Purchases by Material / Account</Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        By Material: each grade and who you bought it from. By Account: each supplier and what they sold you. Total lbs + avg cost.
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            type="date" label="From" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            type="date" label="To" fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            select label="Group by" fullWidth
            value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
          >
            <MenuItem value="material">By Material</MenuItem>
            <MenuItem value="account">By Account</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Button variant="contained" fullWidth onClick={viewPDF}>View PDF</Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ================= BROKER MATERIAL BOX (date range + broker picker) ================= */
function BrokerMaterialBox() {
  const today = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [brokers, setBrokers] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api.get("/brokers")
      .then((r) => setBrokers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load brokers:", e));
  }, []);

  const viewPDF = () => {
    const params = new URLSearchParams({ fromDate, toDate });
    if (picked?.brokerName) params.set("brokerName", picked.brokerName);
    openAuthPdf(`pdfReports/brokerMaterial?${params.toString()}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Broker Material</Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Material a broker brought (from all source yards). Leave broker empty for all brokers.
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="From" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            type="date" label="To" fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button variant="contained" fullWidth onClick={viewPDF}>View PDF</Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Autocomplete
            options={brokers}
            getOptionLabel={(o) => o?.brokerName || ""}
            isOptionEqualToValue={(opt, val) => opt.brokerName === val.brokerName}
            value={picked}
            onChange={(_, v) => setPicked(v)}
            renderInput={(params) => (
              <TextField {...params} label="Broker (optional)" placeholder="All brokers" fullWidth />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ================= BUTTON-ONLY BOX (no date range) ================= */
function ViewPDFBox({ title, endpoint, subtitle }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: subtitle ? 0 : 1 }}>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
          {subtitle}
        </Typography>
      )}
      <Button variant="contained" fullWidth onClick={() => openAuthPdf(endpoint)}>
        View PDF
      </Button>
    </Paper>
  );
}

/* ================= COLLAPSIBLE SECTION ================= */
function Section({ title, count, color, defaultExpanded = true, children }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
          <Chip
            label={`${count} report${count === 1 ? "" : "s"}`}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: color || "primary.light",
              color: color ? "#fff" : "primary.dark",
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

/* ================= MAIN COMPONENT ================= */
export default function PDFDashboard() {
  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Reports</Typography>

      {/* === Purchases === */}
      <Section title="Purchases" count={4} color="success.main">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InvoiceBox title="Purchase Invoice" endpoint="pdfReports/purchaseInvoices" partyLabel="Supplier" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DateRangeBox title="Purchase Invoice Average" endpoint="pdfReports/purchaseInvoices/average" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DateRangeBox title="Purchase Orders" endpoint="pdfReports/purchaseOrders" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PurchaseBreakdownBox />
          </Grid>
        </Grid>
      </Section>

      {/* === Sales === */}
      <Section title="Sales" count={3} color="error.main">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InvoiceBox title="Sale Invoice" endpoint="pdfReports/saleInvoices" partyLabel="Customer" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DateRangeBox title="Sale Invoice Average" endpoint="pdfReports/saleInvoices/average" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DateRangeBox title="Sale Orders" endpoint="pdfReports/saleOrders" />
          </Grid>
        </Grid>
      </Section>

      {/* === Accounts === */}
      <Section title="Accounts" count={3} color="info.main">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <GeneralLedgerBox />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DateRangeBox title="Checks Paid" endpoint="pdfReports/checksPaid" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <BrokerMaterialBox />
          </Grid>
        </Grid>
      </Section>

      {/* === Combined & Inventory === */}
      <Section title="Combined & Inventory" count={3} color="warning.main">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DateRangeBox title="Purchase / Sale Invoices" endpoint="pdfReports/purchaseSaleInvoices" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ViewPDFBox
              title="Stock Inventory"
              subtitle="Current balance per material (all-time)"
              endpoint="pdfReports/stockInventory"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ItemLedgerBox />
          </Grid>
        </Grid>
      </Section>
    </Box>
  );
}
